/**
 * "Mehr anzeigen" toggle for variant options with many values
 * (snippets/variant-main-picker.liquid). Sets `data-expanded` on the option's
 * fieldset; CSS hides the extra buttons while it is missing.
 *
 * The variant picker is morphed from fresh server HTML after every selection,
 * which strips `data-expanded` again. The open state is therefore kept per
 * product + option and put back whenever the morph removes it.
 */
const expanded = new Set();

class VariantMore extends HTMLElement {
  connectedCallback() {
    this.fieldset = this.closest('fieldset');
    this.button = this.querySelector('button');
    if (!this.fieldset || !this.button) return;

    this.onClick = () => this.toggle();
    this.button.addEventListener('click', this.onClick);

    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.fieldset, { attributes: true, attributeFilter: ['data-expanded'] });
    this.observer.observe(this, { attributes: true, attributeFilter: ['data-more-label', 'data-more-template', 'data-state-key'] });

    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  get key() {
    return this.dataset.stateKey || '';
  }

  toggle() {
    if (expanded.has(this.key)) expanded.delete(this.key);
    else expanded.add(this.key);
    this.sync();
  }

  /** Counts what is actually hidden: a selected extra value stays visible. */
  moreLabel() {
    const template = this.dataset.moreTemplate;
    if (!template) return this.dataset.moreLabel;
    const hidden = [...this.fieldset.querySelectorAll('.variant-option__button-label--extra')].filter(
      (label) => !label.querySelector('input:checked')
    ).length;
    return template.replace('#', String(hidden));
  }

  sync() {
    const open = expanded.has(this.key);
    if (open !== this.fieldset.hasAttribute('data-expanded')) {
      this.fieldset.toggleAttribute('data-expanded', open);
    }
    this.button.setAttribute('aria-expanded', String(open));
    const label = open ? this.dataset.lessLabel : this.moreLabel();
    if (label && this.button.textContent !== label) this.button.textContent = label;
  }
}

if (!customElements.get('variant-more')) {
  customElements.define('variant-more', VariantMore);
}
